import { RSAA } from 'redux-api-middleware';
import customFailure from 'helpers/customFailureAction';

export const GET_MESSAGE_REQUEST = 'opus/filter/GET_MESSAGE_REQUEST';
export const GET_MESSAGE_SUCCESS = 'opus/filter/GET_MESSAGE_SUCCESS';
export const GET_MESSAGE_FAILURE = 'opus/filter/GET_MESSAGE_FAILURE';

export const SET_MESSAGE_REQUEST = 'opus/filter/SET_MESSAGE_REQUEST';
export const SET_MESSAGE_SUCCESS = 'opus/filter/SET_MESSAGE_SUCCESS';
export const SET_MESSAGE_FAILURE = 'opus/filter/SET_MESSAGE_FAILURE';

export type MessageActionTypes =
  | typeof GET_MESSAGE_REQUEST
  | typeof GET_MESSAGE_SUCCESS
  | typeof GET_MESSAGE_FAILURE
  | typeof SET_MESSAGE_REQUEST
  | typeof SET_MESSAGE_SUCCESS
  | typeof SET_MESSAGE_FAILURE;

interface IMessage {
  message: any;
  messageId: string;
  error?: string;
  errorInfo?: any;
}

interface IMessagesAction {
  type: MessageActionTypes;
  payload: IMessage;
}

interface IMessageState {
  loading: boolean;
  success: boolean;
  error: Error | null;
  errorInfo: any;
  value: any;
}

interface IMessagesState {
  [key: string]: IMessageState;
}

export const initialState: IMessagesState = {};

export default function reducer(
  state: IMessagesState = initialState,
  action: IMessagesAction
): IMessagesState {
  switch (action.type) {
    case GET_MESSAGE_REQUEST:
    case SET_MESSAGE_REQUEST:
      return {
        ...state,
        [action.payload.messageId]: {
          ...state[action.payload.messageId],
          loading: true,
          success: false,
          error: null,
          errorInfo: null,
        },
      };

    case GET_MESSAGE_SUCCESS:
    case SET_MESSAGE_SUCCESS:
      return {
        ...state,
        [action.payload.messageId]: {
          loading: false,
          success: true,
          error: null,
          errorInfo: null,
          value: action.payload.message,
        },
      };

    case GET_MESSAGE_FAILURE:
    case SET_MESSAGE_FAILURE:
      return {
        ...state,
        [action.payload.messageId]: {
          ...state[action.payload.messageId],
          loading: false,
          success: false,
          error: new Error(action.payload.error),
          errorInfo: action.payload.errorInfo,
        },
      };

    default:
      return state;
  }
}

/**
 * Retrieve the selected message from the API and saves it to the store.
 */
export const getMessage = (messageId: string): any => {
  const endpoint = `/api/application/opus/message/${messageId}`;

  return {
    [RSAA]: {
      types: [
        {
          type: GET_MESSAGE_REQUEST,
          payload: (): any => ({ messageId }),
        },
        GET_MESSAGE_SUCCESS,
        {
          type: GET_MESSAGE_FAILURE,
          payload: async (
            action: IMessagesAction,
            state: IMessagesState,
            res: any
          ): Promise<any> => {
            const failurePayload = await customFailure(res);

            return { ...failurePayload, messageId };
          },
        },
      ],
      endpoint,
      method: 'GET',
      credentials: 'same-origin',
    },
  };
};

/**
 * Sends the selected message to the API and saves it to the store.
 */
export const setMessage = (messageId: string, message: IMessage): any => {
  const endpoint = `/api/application/opus/message/${messageId}`;

  return {
    [RSAA]: {
      types: [
        {
          type: SET_MESSAGE_REQUEST,
          payload: (): any => ({ messageId }),
        },
        {
          type: SET_MESSAGE_SUCCESS,
          payload: (): any => ({ messageId, message }),
        },
        {
          type: SET_MESSAGE_FAILURE,
          payload: async (
            action: IMessagesAction,
            state: IMessagesState,
            res: any
          ): Promise<any> => {
            const failurePayload = await customFailure(res);

            return { ...failurePayload, messageId };
          },
        },
      ],
      endpoint,
      method: 'PUT',
      body: JSON.stringify({ message, messageId }),
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
    },
  };
};
